<template>
  <div class="tool-requests-page bg-neutral-100 min-h-screen">
    <!-- 页面头部 -->
    <section class="py-12 bg-white border-b border-neutral-200">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <h1 class="text-3xl md:text-4xl font-bold text-neutral-900 mb-2">
              工具需求
            </h1>
            <p class="text-neutral-500">
              浏览社区的需求，寻找灵感或推荐合适的工具
            </p>
          </div>
          <router-link
            to="/request"
            class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium flex items-center justify-center space-x-2"
          >
            <i class="fa fa-plus"></i>
            <span>提出需求</span>
          </router-link>
        </div>
      </div>
    </section>

    <!-- 搜索筛选区域 -->
    <section class="py-6 bg-white border-b border-neutral-200">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row gap-4">
          <!-- 搜索框 -->
          <div class="flex-1 relative">
            <input
              type="text"
              v-model="searchForm.searchKey"
              @input="handleSearch"
              placeholder="搜索需求标题..."
              class="w-full px-4 py-2.5 pl-10 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
            />
            <i
              class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
            ></i>
          </div>

          <!-- 分类筛选 -->
          <div class="w-full md:w-48 relative">
            <select
              v-model="searchForm.categoryId"
              @change="handleSearch"
              class="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 appearance-none bg-white"
            >
              <option value="">全部分类</option>
              <option
                v-for="item in categories"
                :value="item.id"
                :key="item.id"
              >
                {{ item.title }}
              </option>
            </select>
            <i
              class="fa fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm pointer-events-none"
            ></i>
          </div>

          <!-- 状态筛选 -->
          <div class="w-full md:w-40 relative">
            <select
              v-model="searchForm.status"
              @change="handleSearch"
              class="w-full px-4 py-2.5 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 appearance-none bg-white"
            >
              <option value="-1">全部状态</option>
              <option value="0">进行中</option>
              <option value="1">已解决</option>
            </select>
            <i
              class="fa fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm pointer-events-none"
            ></i>
          </div>
        </div>
      </div>
    </section>

    <!-- 需求列表 -->
    <section class="py-8">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <!-- 列表内容 -->
        <div v-if="requestList.length > 0" class="space-y-4">
          <div
            v-for="request in requestList"
            :key="request.id"
            class="bg-white rounded-xl p-5 md:p-6 border border-neutral-200 hover:shadow-md transition-shadow duration-200"
          >
            <div
              class="flex flex-col md:flex-row md:items-start md:justify-between gap-4"
            >
              <!-- 左侧内容 -->
              <div class="flex-1">
                <div class="flex items-start gap-3 mb-3">
                  <h3
                    class="font-semibold text-lg text-neutral-900 hover:text-primary transition-colors cursor-pointer"
                  >
                    {{ request.title }}
                  </h3>
                  <span
                    class="text-xs px-2 py-1 rounded-full whitespace-nowrap"
                    :class="{
                      'bg-yellow-100 text-yellow-700': request.status === 0,
                      'bg-green-100 text-green-700': request.status === 1,
                    }"
                  >
                    {{ getStatusText(request.status) }}
                  </span>
                </div>
                <p
                  class="text-sm text-neutral-500 mb-4 whitespace-pre-line line-clamp-3"
                >
                  {{ request.content }}
                </p>
                <div
                  class="flex flex-wrap items-center gap-4 text-xs text-neutral-400"
                >
                  <span class="flex items-center space-x-1">
                    <img
                      :src="
                        request.avatar || 'https://picsum.photos/id/64/100/100'
                      "
                      alt="头像"
                      class="w-5 h-5 rounded-full"
                    />
                    <span>{{ request.uname || "匿名用户" }}</span>
                  </span>
                  <span class="flex items-center">
                    <i class="fa fa-folder-o mr-1"></i>
                    {{
                      request.categoryName ||
                      getCategoryName(request.categoryId)
                    }}
                  </span>
                  <span class="flex items-center">
                    <i class="fa fa-calendar mr-1"></i>
                    {{ new Date(request.createTime).format("yyyy-MM-dd") }}
                  </span>
                </div>
              </div>

              <!-- 右侧统计 -->
              <div
                class="flex md:flex-col items-center md:items-end gap-4 md:gap-2 text-sm text-neutral-500"
              >
                <span class="flex items-center space-x-1">
                  <i class="fa fa-comment-o text-neutral-400"></i>
                  <span>{{ request.replyCount || 0 }} 回复</span>
                </span>
                <button
                  class="px-4 py-1.5 bg-primary/10 text-primary text-sm rounded-full hover:bg-primary/20 transition-colors"
                  @click="viewDetail(request)"
                >
                  查看详情
                </button>
                <button
                  class="px-4 py-1.5 rounded-full text-sm border transition-colors"
                  :class="
                    inMyApps(request.id)
                      ? 'border-primary/30 text-primary bg-primary/5'
                      : 'border-neutral-200 text-neutral-500 hover:border-primary hover:text-primary'
                  "
                  :title="
                    inMyApps(request.id)
                      ? '已加入我的应用，点击移出'
                      : '加入我的应用'
                  "
                  @click.stop="toggleMyApp(request.id)"
                >
                  <i
                    :class="inMyApps(request.id) ? 'fa fa-check' : 'fa fa-plus'"
                    class="mr-1"
                  ></i>
                  {{ inMyApps(request.id) ? "已在应用" : "加入应用" }}
                </button>
              </div>
            </div>
          </div>

          <!-- 分页 -->
          <div class="flex justify-center pt-6">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              :page-size="pageSize"
              :current-page="page"
              @current-change="handlePageChange"
            />
          </div>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="bg-white rounded-xl p-16 border border-neutral-200 text-center"
        >
          <div
            class="w-20 h-20 mx-auto mb-6 rounded-full bg-neutral-100 flex items-center justify-center"
          >
            <i class="fa fa-search text-4xl text-neutral-400"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-700 mb-2">
            暂无相关需求
          </h3>
          <p class="text-sm text-neutral-500 mb-6">
            没有找到符合条件的需求，试试其他筛选条件
          </p>
          <button
            @click="resetSearch"
            class="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
          >
            重置筛选
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { globalState } from "@/state";
import myAppsMixin from "@/utils/myApps";

export default {
  name: "ToolRequestsPage",
  mixins: [myAppsMixin("request")],
  data() {
    return {
      requestList: [],
      categories: [],
      page: 1,
      pageSize: 10,
      total: 0,
      searchForm: {
        keyword: "",
        categoryId: "",
        status: -1,
      },
      searchTimer: null,
      state: globalState,
    };
  },
  created() {
    this.getCategories();
    this.getRequestList();
  },
  methods: {
    // 获取分类列表
    async getCategories() {
      try {
        const res = await this.$axios({
          url: "demandcat/list",
        });
        if (res.code === 200) {
          this.categories = res.data || [];
        }
      } catch (error) {
        console.error("获取分类失败:", error);
      }
    },

    // 获取需求列表
    async getRequestList() {
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
        };

        // 添加搜索条件
        if (this.searchForm.keyword) {
          params.searchKey = this.searchForm.keyword;
        }
        if (this.searchForm.categoryId) {
          params.categoryId = this.searchForm.categoryId;
        }
        if (this.searchForm.status !== "") {
          params.status = this.searchForm.status;
        }

        const data = await this.$aesEncrypt(params);
        const res = await this.$axios({
          url: "demand/list",
          method: "POST",
          data: {
            data: data,
          },
        });

        if (res.code === 200) {
          this.requestList = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (error) {
        console.error("获取需求列表失败:", error);
        this.requestList = [];
        this.total = 0;
      }
    },

    // 搜索（防抖）
    handleSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getRequestList();
      }, 300);
    },

    // 分页切换
    handlePageChange(page) {
      this.page = page;
      this.getRequestList();
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        keyword: "",
        categoryId: "",
        status: "",
      };
      this.page = 1;
      this.getRequestList();
    },

    // 获取状态文本
    getStatusText(status) {
      const map = {
        0: "进行中",
        1: "已解决",
      };
      return map[status] || "未知状态";
    },

    // 获取分类名称
    getCategoryName(id) {
      const category = this.categories.find((c) => c.id === id);
      return category ? category.title : "未知分类";
    },

    // 查看详情
    viewDetail(request) {
      this.state.openRequestModal(request.id, this.categories);
    },
  },
};
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
